
import {
    createStartAudioOnlyEvent,
    createStartMutedConfigurationEvent,
    createSyncTrackStateEvent,
    createTrackMutedEvent,
    sendAnalytics
} from '../../analytics';
import { APP_STATE_CHANGED } from '../../mobile/background';
import { NOTIFICATION_TIMEOUT_TYPE, showWarningNotification } from '../../notifications';
import { BACKGROUND_ENABLED } from '../../virtual-background/actionTypes';
import { isMediaBlockedForLocal } from '../../webinar/functions';
import { SET_AUDIO_ONLY, setAudioOnly } from '../audio-only';
import { CONFERENCE_FAILED, CONFERENCE_LEFT, SET_ROOM } from '../conference/actionTypes';
import { isRoomValid } from '../conference/functions';
import { MiddlewareRegistry } from '../redux';
import { getPropertyValue } from '../settings';
import { TRACK_ADDED, TRACK_UPDATED } from '../tracks/actionTypes';
import { trackMuteUnmuteFailed } from '../tracks/actions';
import {
    getLocalDesktopTrack,
    getLocalVideoTrack,
    isLocalTrackMuted,
    isScreenMediaShared,
    setTrackMuted
} from '../tracks/functions';

import {
    SET_AUDIO_MUTED,
    SET_CAMERA_ZOOM,
    SET_SCREENSHARE_MUTED,
    SET_SCREENSHARE_ZOOM,
    SET_VIDEO_MUTED,
    SET_VIDEO_UNMUTE_PERMISSIONS,
    TOGGLE_CAMERA_FACING_MODE
} from './actionTypes';
import { setAudioMuted, setCameraFacingMode, setScreenshareMuted, setVideoMuted } from './actions';
import { applyStoredCameraZoom } from './cameraZoom';
import {
    CAMERA_FACING_MODE,
    MEDIA_TYPE,
    SCREENSHARE_MUTISM_AUTHORITY,
    VIDEO_MUTISM_AUTHORITY
} from './constants';
import { getStartWithAudioMuted, getStartWithVideoMuted } from './functions';
import logger from './logger';
import {
    _AUDIO_INITIAL_MEDIA_STATE,
    _VIDEO_INITIAL_MEDIA_STATE
} from './reducer';
import './subscriber';


/**
 * Implements the entry point of the middleware of the feature base/media.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case APP_STATE_CHANGED:
        return _appStateChanged(store, next, action);

    case BACKGROUND_ENABLED:
    case CONFERENCE_FAILED:
    case CONFERENCE_LEFT:
    case SET_CAMERA_ZOOM:
    case SET_SCREENSHARE_ZOOM: {
        // Zoom changes apply the new target; leaving resets it to 1x; a
        // virtual background toggle (web) swaps the effect that draws the
        // crop, so the zoom is re-applied once the toggle has settled.
        const result = next(action);

        _applyCaptureZoom(store);

        return result;
    }

    case SET_AUDIO_ONLY:
        return _setAudioOnly(store, next, action);

    case SET_ROOM:
        return _setRoom(store, next, action);

    // "Block everyone's mic and camera" has to hold against every way to
    // unmute, not just the toolbar button it disables: the keyboard shortcut,
    // the external API and the device settings all end up here. Swallowing
    // the unmute is the same thing upstream does for its force-mute.
    case SET_AUDIO_MUTED:
    case SET_VIDEO_MUTED: {
        if (!action.muted && isMediaBlockedForLocal(store.getState())) {
            return;
        }
        break;
    }

    case SET_SCREENSHARE_MUTED: {
        // const state = store.getState();
        // const participant = getLocalParticipant(state);

        // if (!action.muted && isForceMuted(participant, AVM_MEDIA_TYPE.DESKTOP, state)) {
        //     return;
        // }
        break;
    }

    case SET_VIDEO_UNMUTE_PERMISSIONS: {
        const { blocked, skipNotification } = action;
        const state = store.getState();
        const tracks = state['features/base/tracks'];
        const isVideoMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);
        const isMediaShared = isScreenMediaShared(state);

        if (blocked && isVideoMuted && !isMediaShared && !skipNotification) {
            store.dispatch(showWarningNotification({
                descriptionKey: 'notify.videoUnmuteBlockedDescription',
                titleKey: 'notify.videoUnmuteBlockedTitle'
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        }
        break;
    }

    case TRACK_ADDED: {
        const result = next(action);
        const { track } = action;

        // Don't sync track mute state with the redux store for screenshare
        // since video mute state represents local camera mute state only.
        track.local && track.videoType !== 'desktop'
            && _syncTrackMutedState(store, track);

        if (track.local && track.mediaType !== MEDIA_TYPE.AUDIO) {
            // Let other TRACK_ADDED listeners (virtual background re-attach,
            // capturer start) settle before applying the stored zoom to the
            // new camera or screenshare capture.
            setTimeout(() => _applyCaptureZoom(store), 250);
        }

        return result;
    }

    case TRACK_UPDATED: {
        const result = next(action);
        const mhTrack = action.track?.mhTrack;

        // Unmute restarts the capture and resets its zoom to 1x. Restore the
        // stored factor after the camera / share is running again.
        if (action.track?.muted === false
                && mhTrack
                && typeof mhTrack.isLocal === 'function'
                && mhTrack.isLocal()
                && typeof mhTrack.isVideoTrack === 'function'
                && mhTrack.isVideoTrack()) {
            setTimeout(() => _applyCaptureZoom(store), 250);
        }

        return result;
    }

    case TOGGLE_CAMERA_FACING_MODE: {
        const result = next(action);

        // A different camera has its own zoom range; start it at 1x like the
        // system Camera app, then push that to the new capturer.
        _applyCaptureZoom(store);

        return result;
    }
    }

    return next(action);
});

/**
 * Adjusts the video muted state based on the app state.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code APP_STATE_CHANGED} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _appStateChanged({ dispatch, getState }, next, action) {
    if (navigator.product === 'ReactNative') {
        const { appState } = action;

        if (appState === 'active') {
            // Check and recreate ALL local video tracks (camera + screenshare)
            // that may have died while the app was in background / phone call.
            _checkAndRecreateLocalVideoTracks(dispatch, getState, 500);

            // Nudge remote tracks whose underlying MediaStreamTrack went to
            // "ended" while the app was backgrounded.  We can't recreate
            // remote tracks (they come from the server), but we can dispatch
            // TRACK_UPDATED so the UI re-evaluates them once the bridge
            // resumes sending video after lastN is restored.
            _refreshRemoteVideoTracks(dispatch, getState);

            // Some devices keep the camera track as 'live' and enabled after
            // unlock but produce black frames. Force a health-check after a
            // longer delay to catch these edge cases — if the track is still
            // producing frames, recreation is a no-op since the track will
            // pass all health checks in _maybeRecreateTrack.
            _forceTrackHealthCheck(dispatch, getState, 1000);

            // Re-select participants after returning to foreground so the
            // bridge knows which remote video streams to prioritize. During
            // background, lastN was 0 and the bridge stopped sending video.
            // The lastN middleware restores the value on APP_STATE_CHANGED,
            // but explicitly re-selecting ensures remote streams resume
            // promptly—especially after long background periods or phone calls.
            setTimeout(() => {
                const { selectParticipant, selectParticipantInLargeVideo }
                    = require('../../large-video/actions');

                dispatch(selectParticipantInLargeVideo());
                dispatch(selectParticipant());
            }, 1000);
        }
    }

    return next(action);
}

/**
 * Checks if local video tracks (camera and screenshare) need recreation after
 * the app returns to foreground.  The OS may kill camera hardware during phone
 * calls, lock, or background — MediaStreamTrack goes to "ended" or gets its
 * enabled flag flipped by the OS.  Uses progressive retry because the OS may
 * take varying amounts of time to release camera hardware.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @param {number} delay - Delay in ms before checking.
 * @param {number} [attempt=1] - Current attempt number.
 * @private
 * @returns {void}
 */
function _checkAndRecreateLocalVideoTracks(dispatch, getState, delay, attempt = 1) {
    const MAX_ATTEMPTS = 3;

    setTimeout(() => {
        const state = getState();
        const tracks = state['features/base/tracks'];
        const retryCtx = {
            dispatch,
            getState,
            attempt,
            delay,
            maxAttempts: MAX_ATTEMPTS
        };

        // --- Camera track ---
        const localVideoTrack = getLocalVideoTrack(tracks);

        if (localVideoTrack && localVideoTrack.mhTrack && !localVideoTrack.muted) {
            _maybeRecreateTrack(localVideoTrack, 'camera', retryCtx);
        }

        // --- Screenshare (desktop) track ---
        const localDesktopTrack = getLocalDesktopTrack(tracks);

        if (localDesktopTrack && localDesktopTrack.mhTrack && !localDesktopTrack.muted) {
            _maybeRecreateTrack(localDesktopTrack, 'desktop', retryCtx);
        }

        // Schedule one more check on first attempt even if tracks look fine,
        // because some devices report the track as healthy briefly before it
        // actually fails (delayed release after phone call).
        if (attempt === 1) {
            _checkAndRecreateLocalVideoTracks(dispatch, getState, 1500, attempt + 1);
        }
    }, delay);
}

/**
 * Performs a forced health check on the local camera track after a longer
 * delay.  Some devices (particularly certain Android OEMs and older iOS)
 * keep the MediaStreamTrack reporting 'live', enabled, and not muted after
 * unlock — but produce black frames or the buffer freezes.  We detect this
 * by checking zero dimensions, and if a simple enabled-toggle nudge doesn't
 * restore frames we force-recreate the track as a last resort.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @param {number} delay - Delay in ms before running the check.
 * @private
 * @returns {void}
 */
function _forceTrackHealthCheck(dispatch, getState, delay) {
    setTimeout(() => {
        const state = getState();
        const tracks = state['features/base/tracks'];
        const localVideoTrack = getLocalVideoTrack(tracks);

        if (!localVideoTrack || !localVideoTrack.mhTrack || localVideoTrack.muted) {
            return;
        }

        const mhTrack = localVideoTrack.mhTrack;
        const nativeTrack = mhTrack.getTrack && mhTrack.getTrack();

        if (!nativeTrack || nativeTrack.readyState !== 'live') {
            return; // Already 'ended' — regular recreation handles this.
        }

        // On some devices, getSettings() returns zero width/height when the
        // camera hardware is not actually capturing.
        const settings = nativeTrack.getSettings ? nativeTrack.getSettings() : {};

        if (settings.width === 0 || settings.height === 0) {
            logger.info('Local camera track has zero dimensions after unlock — recreating');
            _maybeForceRecreateCamera(dispatch, getState, mhTrack);

            return;
        }

        // Attempt to re-toggle the enabled flag as a nudge for devices that
        // freeze the camera pipeline without reporting any detectable state change.
        if (nativeTrack.enabled) {
            nativeTrack.enabled = false;

            // Small delay to let the hardware reset, then re-enable.
            setTimeout(() => {
                nativeTrack.enabled = true;

                // After the nudge, wait and re-check — if the track is still
                // producing zero-dimension frames or has gone dead, recreate.
                _verifyTrackAfterNudge(dispatch, getState, 1500);
            }, 100);
        } else {
            // Track was disabled unexpectedly — force recreate.
            logger.info('Local camera track disabled unexpectedly — recreating');
            _maybeForceRecreateCamera(dispatch, getState, mhTrack);
        }
    }, delay);
}

/**
 * Re-checks the local camera track after an enabled-toggle nudge.  If the
 * track is still frozen (zero dimensions, ended, or disabled) we force-
 * recreate it.  This is the follow-up that ensures the nudge actually
 * restored the camera pipeline.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @param {number} delay - Delay in ms before verifying.
 * @private
 * @returns {void}
 */
function _verifyTrackAfterNudge(dispatch, getState, delay) {
    setTimeout(() => {
        const tracks = getState()['features/base/tracks'];
        const localVideoTrack = getLocalVideoTrack(tracks);

        if (!localVideoTrack || !localVideoTrack.mhTrack || localVideoTrack.muted) {
            return;
        }

        const mhTrack = localVideoTrack.mhTrack;
        const nativeTrack = mhTrack.getTrack && mhTrack.getTrack();

        if (!nativeTrack) {
            return;
        }

        // Track died after the nudge — recreate.
        if (nativeTrack.readyState !== 'live' || !nativeTrack.enabled) {
            logger.info('Local camera track still unhealthy after nudge — recreating');
            _maybeForceRecreateCamera(dispatch, getState, mhTrack);

            return;
        }

        // Still reporting zero dimensions — camera hardware didn't recover.
        const settings = nativeTrack.getSettings ? nativeTrack.getSettings() : {};

        if (settings.width === 0 || settings.height === 0) {
            logger.info('Local camera track still has zero dimensions after nudge — recreating');
            _maybeForceRecreateCamera(dispatch, getState, mhTrack);
        }
    }, delay);
}

/**
 * Force-recreates the local camera track.  Used as a last-resort fallback
 * when the track is technically 'live' but not producing usable frames.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @param {Object} mhTrack - The MH track to replace.
 * @private
 * @returns {void}
 */
function _maybeForceRecreateCamera(dispatch, getState, mhTrack) {
    _recreateCamera(dispatch, getState, mhTrack)
        .catch(err => {
            logger.error('Force camera recreation failed:', err);

            // This path makes a single attempt; the camera it gives up on is sending no frames.
            _endDeadCamera(dispatch, getState, mhTrack);
        });
}

/**
 * The dead tracks a recreation is in progress for. The foreground check runs twice on purpose and the zero-frame
 * check can fire alongside it, so the same dead track can be picked up again while the first recreation is still
 * waiting on the capture; for a screenshare that put a second system capture prompt on screen.
 *
 * @type {WeakSet<Object>}
 */
const recreating = new WeakSet();

/**
 * Runs a recreation of a dead track unless one is already in progress for it.
 *
 * @param {Object} mhTrack - The dead track.
 * @param {Function} recreate - Starts the recreation, returning its Promise.
 * @private
 * @returns {Promise<void>}
 */
function _recreateOnce(mhTrack, recreate) {
    if (recreating.has(mhTrack)) {
        return Promise.resolve();
    }
    recreating.add(mhTrack);

    return recreate().finally(() => recreating.delete(mhTrack));
}

/**
 * Opens the camera again and puts it in place of a dead one.
 *
 * It reopens the camera the user is on: the facing mode is the one the flip button keeps in the store, which
 * _switchCamera changes without touching the track. The device id is not passed - it still names the camera the
 * track was opened with, so after a flip it would bring the other camera back.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @param {Object} mhTrack - The dead camera track.
 * @private
 * @returns {Promise<void>}
 */
function _recreateCamera(dispatch, getState, mhTrack) {
    const { replaceLocalTrack } = require('../tracks/actions');
    const { createLocalTracksF } = require('../tracks/mhFunctions');

    return _recreateOnce(mhTrack, () => createLocalTracksF(
        { devices: [ MEDIA_TYPE.VIDEO ],
            facingMode: getState()['features/base/media'].video.facingMode || CAMERA_FACING_MODE.USER },
        { dispatch,
            getState })
        .then(newTracks => {
            const [ newTrack ] = newTracks || [];

            if (!newTrack) {
                return;
            }

            // Opening the camera takes a while. If it was replaced meanwhile, putting this one in its place would
            // undo that.
            const current = getLocalVideoTrack(getState()['features/base/tracks']);

            if (current?.mhTrack !== mhTrack || mhTrack.disposed) {
                return newTrack.dispose();
            }

            // If the user turned the camera off meanwhile, the new camera still replaces the dead one, but off:
            // turning the camera on only re-enables the track in place, so keeping the dead one would bring back
            // no video. The replace takes the mute state from the new track, so the user's choice stands.
            if (current.muted) {
                return newTrack.mute().then(() => dispatch(replaceLocalTrack(mhTrack, newTrack)));
            }

            return dispatch(replaceLocalTrack(mhTrack, newTrack));
        }));
}

/**
 * Captures the screen again and puts it in place of a dead screenshare, the same way the share button starts one.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @param {Object} mhTrack - The dead screenshare track.
 * @private
 * @returns {Promise<void>}
 */
function _recreateScreenshare(dispatch, getState, mhTrack) {
    const MeetHourJS = require('../lib-meet-hour').default;
    const { replaceLocalTrack } = require('../tracks/actions');

    return _recreateOnce(mhTrack, () => MeetHourJS.createLocalTracks({ devices: [ 'desktop' ] })
        .then(newTracks => {
            const [ newTrack ] = newTracks || [];

            if (!newTrack) {
                return;
            }

            // The capture prompt can stay up for a while. If the user stopped sharing meanwhile, putting this
            // capture in place would start sharing again against their decision: the dead track is already off
            // the conference, and replacing it falls back to adding the new one.
            const current = getLocalDesktopTrack(getState()['features/base/tracks']);

            if (current?.mhTrack !== mhTrack || current.muted || mhTrack.disposed) {
                return newTrack.dispose();
            }

            return dispatch(replaceLocalTrack(mhTrack, newTrack));
        }));
}

/**
 * Turns off a camera whose capture died and could not be brought back, the same way the camera button does.
 * Otherwise the camera stays marked as on while it sends nothing, and the button offers to turn it off.
 *
 * The dead track is taken out as well. On mobile the camera is turned off and on by flipping track.enabled on the
 * same track, so leaving it in place would make the next "camera on" re-enable the dead track - still no video.
 * Without a track, turning the camera on opens a new one, as it does for a user who joined with it off.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @param {Object} mhTrack - The dead camera track.
 * @private
 * @returns {void}
 */
function _endDeadCamera(dispatch, getState, mhTrack) {
    const current = getLocalVideoTrack(getState()['features/base/tracks']);

    if (current?.mhTrack !== mhTrack || current.muted) {
        return;
    }

    const { replaceLocalTrack } = require('../tracks/actions');

    // replaceLocalTrack reports its own failures; the camera is marked off either way.
    Promise.resolve(dispatch(replaceLocalTrack(mhTrack, null)))
        .catch(() => undefined)
        .then(() => dispatch(setVideoMuted(true, MEDIA_TYPE.VIDEO, VIDEO_MUTISM_AUTHORITY.USER)));
}

/**
 * Ends a screenshare whose capture died and could not be brought back, the same way the stop button does.
 * Otherwise the share stays marked as on - the indicator and the stop button say sharing while nothing is sent,
 * and starting a new share is not offered.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @param {Object} mhTrack - The dead screenshare track.
 * @private
 * @returns {void}
 */
function _endDeadScreenshare(dispatch, getState, mhTrack) {
    const current = getLocalDesktopTrack(getState()['features/base/tracks']);

    if (current?.mhTrack === mhTrack && !current.muted) {
        dispatch(setScreenshareMuted(true));
    }
}

/**
 * Returns true if the user themselves refused the capture, which no amount of retrying can change.
 *
 * @param {Error} error - The track creation error.
 * @private
 * @returns {boolean}
 */
function _isUserRejection(error) {
    const { MHTrackErrors } = require('../lib-meet-hour');

    return error?.name === MHTrackErrors.SCREENSHARING_USER_CANCELED
        || error?.name === MHTrackErrors.PERMISSION_DENIED;
}

/**
 * Inspects a single local video track and recreates it if the underlying
 * MediaStreamTrack is dead (readyState === 'ended') or was disabled by the OS.
 *
 * @param {Object} localTrack - The redux track object.
 * @param {string} kind - 'camera' or 'desktop', for logging.
 * @param {Object} ctx - Retry context: { dispatch, getState, attempt, delay, maxAttempts }.
 * @private
 * @returns {void}
 */
function _maybeRecreateTrack(localTrack, kind, ctx) {
    const { dispatch, getState, attempt, delay, maxAttempts } = ctx;
    const mhTrack = localTrack.mhTrack;
    const nativeTrack = mhTrack.getTrack && mhTrack.getTrack();

    if (!nativeTrack) {
        return;
    }

    // Recreate the track if:
    // 1. readyState is 'ended' — killed by the OS during lock/phone call.
    // 2. Track is 'live' but enabled flag was turned off by the OS during
    //    a phone call interruption (camera only — screenshare uses stream
    //    removal for muting, so enabled is not the mute mechanism).
    // 3. Track is 'live' but OS-muted (MediaStreamTrack.muted === true) —
    //    the OS suspended the camera hardware (e.g. lock screen on iOS/Android)
    //    without ending the track. The track produces black frames in this state.
    const isEnded = nativeTrack.readyState === 'ended';
    const isDisabledByOS = kind === 'camera'
        && nativeTrack.readyState === 'live'
        && !nativeTrack.enabled;
    const isOSMuted = kind === 'camera'
        && nativeTrack.readyState === 'live'
        && nativeTrack.muted === true;

    if (!isEnded && !isDisabledByOS && !isOSMuted) {
        return;
    }

    logger.info(`Local ${kind} track needs recreation (attempt ${attempt})`
        + ` — readyState: ${nativeTrack.readyState}`
        + `, enabled: ${nativeTrack.enabled}`
        + `, muted: ${nativeTrack.muted}`);

    if (kind === 'camera') {
        _recreateCamera(dispatch, getState, mhTrack)
            .catch(err => {
                // A denied camera permission will not change by asking again on a timer.
                if (_isUserRejection(err)) {
                    logger.info('Camera track recreation was denied by the user; not retrying.');
                    _endDeadCamera(dispatch, getState, mhTrack);

                    return;
                }

                logger.error(`Failed to recreate camera track (attempt ${attempt}):`, err);

                if (attempt >= maxAttempts) {
                    _endDeadCamera(dispatch, getState, mhTrack);
                } else {
                    setTimeout(() => {
                        const refreshedTrack = getLocalVideoTrack(
                            getState()['features/base/tracks']);

                        if (refreshedTrack && refreshedTrack.mhTrack && !refreshedTrack.muted) {
                            _maybeRecreateTrack(refreshedTrack, 'camera', {
                                dispatch,
                                getState,
                                attempt: attempt + 1,
                                delay: delay * 2,
                                maxAttempts
                            });
                        }
                    }, delay * 2);
                }
            });
    } else {
        _recreateScreenshare(dispatch, getState, mhTrack)
            .catch(err => {
                // Declining the system screen-capture prompt is an answer, not a transient failure. Retrying
                // re-opens the prompt, so the user who said no got asked twice more.
                if (_isUserRejection(err)) {
                    logger.info('Desktop track recreation was declined by the user; not retrying.');
                    _endDeadScreenshare(dispatch, getState, mhTrack);

                    return;
                }

                logger.error(`Failed to recreate desktop track (attempt ${attempt}):`, err);

                if (attempt >= maxAttempts) {
                    _endDeadScreenshare(dispatch, getState, mhTrack);
                } else {
                    setTimeout(() => {
                        const refreshedTrack = getLocalDesktopTrack(
                            getState()['features/base/tracks']);

                        if (refreshedTrack && refreshedTrack.mhTrack && !refreshedTrack.muted) {
                            _maybeRecreateTrack(refreshedTrack, 'desktop', {
                                dispatch,
                                getState,
                                attempt: attempt + 1,
                                delay: delay * 2,
                                maxAttempts
                            });
                        }
                    }, delay * 2);
                }
            });
    }
}

/**
 * After returning to foreground, nudge remote video tracks whose underlying
 * MediaStreamTrack may have gone stale while the app was backgrounded.
 *
 * We cannot recreate remote tracks (they are owned by the server / remote
 * peer), but dispatching TRACK_UPDATED forces React components to re-render
 * and the PiP module to refresh its stream references.  The bridge will
 * resume sending frames once lastN is restored by the lastN middleware.
 *
 * IMPORTANT: Remote mute state must come from mhTrack.isMuted() (XMPP
 * signaling), NOT from nativeTrack.readyState/enabled.  The receiver
 * MediaStreamTrack stays 'live' and enabled even when the remote user has
 * turned off their camera — using nativeTrack state would incorrectly
 * unmute tracks whose camera is genuinely off, causing black tiles.
 *
 * @param {Function} dispatch - Redux dispatch.
 * @param {Function} getState - Redux getState.
 * @private
 * @returns {void}
 */
function _refreshRemoteVideoTracks(dispatch, getState) {
    setTimeout(() => {
        const tracks = getState()['features/base/tracks'];

        tracks.forEach(t => {
            if (t.local || !t.mhTrack) {
                return;
            }

            // Only touch video / screenshare tracks
            if (t.mediaType !== MEDIA_TYPE.VIDEO && t.mediaType !== MEDIA_TYPE.SCREENSHARE) {
                return;
            }

            // Use mhTrack.isMuted() as the authoritative source of truth.
            // It reflects the XMPP signaling state which correctly tracks
            // whether the remote user has their camera on or off.
            const actualMuted = t.mhTrack.isMuted();

            if (t.muted !== actualMuted) {
                dispatch({
                    type: TRACK_UPDATED,
                    track: {
                        mhTrack: t.mhTrack,
                        muted: actualMuted
                    }
                });
            }
        });
    }, 1500);
}

/**
 * Adjusts the video muted state based on the audio-only state.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code SET_AUDIO_ONLY} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _setAudioOnly({ dispatch }, next, action) {
    const { audioOnly } = action;

    sendAnalytics(createTrackMutedEvent('video', 'audio-only mode', audioOnly));

    // Make sure we mute both the desktop and video tracks.
    dispatch(setVideoMuted(audioOnly, VIDEO_MUTISM_AUTHORITY.AUDIO_ONLY));
    dispatch(setScreenshareMuted(audioOnly, SCREENSHARE_MUTISM_AUTHORITY.AUDIO_ONLY));

    return next(action);
}

/**
 * Notifies the feature base/media that the action {@link SET_ROOM} is being
 * dispatched within a specific redux {@code store}.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action, {@code SET_ROOM}, which is being
 * dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The new state that is the result of the reduction of the
 * specified {@code action}.
 */
function _setRoom({ dispatch, getState }, next, action) {
    // Figure out the desires/intents i.e. the state of base/media. There are
    // multiple desires/intents ordered by precedence such as server-side
    // config, config overrides in the user-supplied URL, user's own app
    // settings, etc.

    const state = getState();
    const { room } = action;
    const roomIsValid = isRoomValid(room);
    const audioMuted = roomIsValid ? getStartWithAudioMuted(state) : _AUDIO_INITIAL_MEDIA_STATE.muted;
    const videoMuted = roomIsValid ? getStartWithVideoMuted(state) : _VIDEO_INITIAL_MEDIA_STATE.muted;

    sendAnalytics(
        createStartMutedConfigurationEvent('local', audioMuted, videoMuted));
    logger.log(
        `Start muted: ${audioMuted ? 'audio, ' : ''}${
            videoMuted ? 'video' : ''}`);

    // Unconditionally express the desires/expectations/intents of the app and
    // the user i.e. the state of base/media. Eventually, practice/reality i.e.
    // the state of base/tracks will or will not agree with the desires.
    dispatch(setAudioMuted(audioMuted));
    dispatch(setCameraFacingMode(CAMERA_FACING_MODE.USER));
    dispatch(setVideoMuted(videoMuted));

    // startAudioOnly
    //
    // FIXME Technically, the audio-only feature is owned by base/conference,
    // not base/media so the following should be in base/conference.
    // Practically, I presume it was easier to write the source code here
    // because it looks like startWithAudioMuted and startWithVideoMuted.
    //
    // XXX After the introduction of the "Video <-> Voice" toggle on the
    // WelcomePage, startAudioOnly is utilized even outside of
    // conferences/meetings.
    const audioOnly
        = Boolean(
            getPropertyValue(
                state,
                'startAudioOnly',
                /* sources */ {
                    // FIXME Practically, base/config is (really) correct
                    // only if roomIsValid. At the time of this writing,
                    // base/config is overwritten by URL params which leaves
                    // base/config incorrect on the WelcomePage after
                    // leaving a conference which explicitly overwrites
                    // base/config with URL params.
                    config: roomIsValid,

                    // XXX We've already overwritten base/config with
                    // urlParams if roomIsValid. However, settings are more
                    // important than the server-side config. Consequently,
                    // we need to read from urlParams anyway. We also
                    // probably want to read from urlParams when
                    // !roomIsValid.
                    urlParams: true,

                    // The following don't have complications around whether
                    // they are defined or not:
                    jwt: false,
                    settings: true
                }));

    sendAnalytics(createStartAudioOnlyEvent(audioOnly));
    logger.log(`Start audio only set to ${audioOnly.toString()}`);

    dispatch(setAudioOnly(audioOnly, false));

    return next(action);
}

/**
 * Syncs muted state of local media track with muted state from media state.
 *
 * @param {Store} store - The redux store.
 * @param {Track} track - The local media track.
 * @private
 * @returns {void}
 */
function _syncTrackMutedState({ getState, dispatch }, track) {
    const state = getState()['features/base/media'];
    const mediaType = track.mediaType;
    const muted = Boolean(state[mediaType].muted);

    // XXX If muted state of track when it was added is different from our media
    // muted state, we need to mute track and explicitly modify 'muted' property
    // on track. This is because though TRACK_ADDED action was dispatched it's
    // not yet in redux state and MHTrackEvents.TRACK_MUTE_CHANGED may be
    // fired before track gets to state.
    if (track.muted !== muted) {
        sendAnalytics(createSyncTrackStateEvent(mediaType, muted));
        logger.log(`Sync ${mediaType} track muted state to ${muted ? 'muted' : 'unmuted'}`);

        track.muted = muted;

        // setTrackMuted rejects on every failure (including an already disposed
        // track), so this needs its own handler or the rejection goes unhandled.
        // Routing it through TRACK_MUTE_UNMUTE_FAILED also lets the local track
        // recovery in base/tracks pick the failure up.
        setTrackMuted(track.mhTrack, muted, state, dispatch)
            .catch(() => dispatch(trackMuteUnmuteFailed(track, muted)));
    }
}

/**
 * Pushes the stored capture zoom onto the live local camera (native and web)
 * and screenshare (web). Idempotent; safe to call after any lifecycle change.
 *
 * @param {Store} store - The redux store.
 * @private
 * @returns {void}
 */
function _applyCaptureZoom(store) {
    try {
        applyStoredCameraZoom(store);
    } catch (error) {
        logger.warn('Failed to apply capture zoom', error);
    }
}
